import { IdleTimer } from "@/components/IdleTimer";
import { Navigation } from "@/components/Navigation";
import { Callout, Toaster } from "@/components/ui";
import { useUniversalChatSocketGlobalHandler } from "@/features/Chat/UniversalClinicChat/hooks/useUniversalChatSocket";
import { useChatSocketGlobalHandler } from "@/features/Chat/hooks/useChatSocket";
import { PatientTabs } from "@/features/PatientTabs/PatientTabs";
import { TooManyTabsModal } from "@/features/PatientTabs/components/TooManyTabsModal";
import { useTooManyTabsWarning } from "@/features/PatientTabs/hooks/useTooManyTabsWarning";
import { useEMRPriming } from "@/hooks/useEMRPriming";
import { useInitializeSockets } from "@/hooks/useInitializeSockets";
import { ErrorBoundary } from "@/lib/monitoring/monitoring";
import { sanitizeRedirect } from "@/lib/redirect";
import { useAsyncMessageSocketEvents } from "@/socket/hooks/useAsyncMessageSocketEvents";
import { useChartSocketEvents } from "@/socket/hooks/useChartSocketEvents";
import { useLiveQueueSocketEvents } from "@/socket/hooks/useLiveQueueSocketEvents";
import { isAuthenticatedAtom } from "@/store/authAtoms";
import { appStore } from "@/store/jotaiStore";
import { Outlet, createRootRoute, redirect } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useAtomValue } from "jotai";

// Component to initialize sockets and attach listeners
// This will only be mounted when the user is authenticated
const SocketInitializer = () => {
  // Initialise all sockets once when authenticated
  useInitializeSockets();

  // Attach feature-specific event listeners using dedicated hooks
  useChartSocketEvents();
  useLiveQueueSocketEvents();
  useAsyncMessageSocketEvents();
  useChatSocketGlobalHandler();
  useUniversalChatSocketGlobalHandler();

  return null; // This component doesn't render anything itself
};

const TooManyTabsWarning = () => {
  const { isOpen, onOpenChange } = useTooManyTabsWarning();
  return <TooManyTabsModal isOpen={isOpen} onOpenChange={onOpenChange} />;
};

const ErrorFallback = () => {
  return (
    <div>
      <Callout
        variant="danger"
        title="An error has occurred"
        description="Please refresh the page. If the problem persists, please log a bug to #care-blocking-bugs or #bugs."
      />
    </div>
  );
};

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    const store = appStore;
    const isAuthenticated = store.get(isAuthenticatedAtom);
    const isLoginPage = location.pathname === "/login";
    const isRootRoute = location.pathname === "/";

    if (!isAuthenticated && !isLoginPage) {
      throw redirect({
        to: "/login",
        // Capture the full href (path + query) so query params like
        // ?encounterId survive the round-trip through login.
        search: {
          redirect: location.href,
        },
      });
    }

    if (isAuthenticated && isLoginPage) {
      // location.search is the raw (unvalidated) search here, so sanitize
      // before trusting it — otherwise an authed user hitting
      // /login?redirect=//evil.com would be sent off-site. Use href so the
      // stored path + query is navigated as a whole.
      const redirectTo = sanitizeRedirect(
        (location.search as { redirect?: unknown }).redirect,
      );
      // redirect()'s type requires `to`, but `href` is the supported way to
      // navigate a fully-built path + query; cast to satisfy the types.
      throw redirect(
        redirectTo
          ? ({ href: redirectTo } as Parameters<typeof redirect>[0])
          : { to: "/live-queue" },
      );
    }

    if (isAuthenticated && isRootRoute) {
      throw redirect({
        to: "/live-queue",
      });
    }
  },
  component: () => {
    const isAuthenticated = useAtomValue(isAuthenticatedAtom);
    useEMRPriming({ enabled: isAuthenticated });

    return (
      <ErrorBoundary fallback={<ErrorFallback />}>
        {isAuthenticated ? (
          <IdleTimer>
            <SocketInitializer />
            <TooManyTabsWarning />
            <div className="flex h-screen w-screen overflow-hidden">
              <div className="h-full w-[50px] bg-primary-green flex flex-col justify-between py-4">
                <Navigation />
              </div>
              <main className="w-full h-full min-w-0 flex flex-col">
                <PatientTabs />
                <Outlet />
              </main>
            </div>
          </IdleTimer>
        ) : (
          <div className="flex h-screen w-screen overflow-hidden">
            <main className="w-full h-full min-w-0 flex flex-col">
              <Outlet />
            </main>
          </div>
        )}
        <Toaster />
        <TanStackRouterDevtools position="bottom-right" />
      </ErrorBoundary>
    );
  },
});
